// src/utils/crypto.js

const DEFAULT_ITER = 310000;
const DEFAULT_SALT = import.meta?.env?.VITE_CRYPTO_SALT || 'jarvisly-salt-v1';

// b64url -> bytes
function b64uToBytes(str) {
  const padLen = (4 - (str.length % 4)) % 4;
  const s = str.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(padLen);
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// PBKDF2 → AES-GCM(256)
async function deriveKey(passphrase, salt = DEFAULT_SALT, iterations = DEFAULT_ITER) {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(salt), iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );
}

// decripta o token gerado no back (JSON {v,iv,data} base64url)
export async function decryptInviteHash(hash, passphrase) {
  const tokenJson = new TextDecoder().decode(b64uToBytes(hash));
  const token = JSON.parse(tokenJson);
  if (!token?.iv || !token?.data) throw new Error('bad_token');

  const iv = b64uToBytes(token.iv); // 12 bytes
  const cipherPlusTag = b64uToBytes(token.data); // ciphertext||authTag(16)

  const key = await deriveKey(passphrase);
  const dec = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    cipherPlusTag
  );
  const txt = new TextDecoder().decode(new Uint8Array(dec));
  return JSON.parse(txt);
}

// Router helpers (BrowserRouter/HashRouter)
export function getSearchFromLocation(loc = window?.location) {
  const search = loc?.search || '';
  if (search) return search;
  const hash = loc?.hash || '';
  const qIndex = hash.indexOf('?');
  return qIndex >= 0 ? hash.slice(qIndex) : '';
}

export function readInviteHashFromLocation(loc = window?.location) {
  const search = getSearchFromLocation(loc);
  const qs = new URLSearchParams(search);

  return qs.get('hash');
}

export function isInviteValid(payload) {
  return typeof payload?.exp === 'number' && Date.now() < payload.exp;
}

export async function loadAndValidateInvite(loc) {
  const hash = readInviteHashFromLocation(loc);
  if (!hash) return { ok: false, reason: 'missing_hash' };

  const passphrase = import.meta.env.VITE_CRYPTO_KEY || 'jarvisly-key-word';
  try {
    const payload = await decryptInviteHash(hash, passphrase);
    if (!isInviteValid(payload)) return { ok: false, reason: 'expired', payload };
    return { ok: true, payload };
  } catch (e) {
    console.warn('[invite] decrypt failed:', e);
    return { ok: false, reason: 'invalid' };
  }
}

/**
 * Check if URL has an invite hash with forceLogout flag
 * Used to determine if we should logout before showing registration page
 * @param {Location} loc - The location object
 * @returns {Promise<{hasForceLogout: boolean, payload: object|null}>}
 */
export async function checkForceLogoutFromHash(loc) {
  const hash = readInviteHashFromLocation(loc);
  if (!hash) return { hasForceLogout: false, payload: null };

  const passphrase = import.meta.env.VITE_CRYPTO_KEY || 'jarvisly-key-word';
  try {
    const payload = await decryptInviteHash(hash, passphrase);
    return { hasForceLogout: !!payload?.forceLogout, payload };
  } catch (e) {
    return { hasForceLogout: false, payload: null };
  }
}

// bytes -> b64url
function bytesToB64u(bytes) {
  const bin = String.fromCharCode(...bytes);
  const b64 = btoa(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// PBKDF2 → AES-GCM(256) for encryption
async function deriveKeyForEncryption(passphrase, salt = DEFAULT_SALT, iterations = DEFAULT_ITER) {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(salt), iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );
}

/**
 * Encrypt a string using AES-GCM with PBKDF2 key derivation
 * @param {string} plaintext - The text to encrypt
 * @param {string} passphrase - The passphrase to use for encryption
 * @returns {Promise<string>} - Base64url encoded encrypted data
 */
export async function encrypt(plaintext, passphrase) {
  const enc = new TextEncoder();
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 12 bytes for GCM
  const key = await deriveKeyForEncryption(passphrase);

  const cipherPlusTag = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    key,
    enc.encode(plaintext)
  );

  const token = {
    v: 1,
    iv: bytesToB64u(iv),
    data: bytesToB64u(new Uint8Array(cipherPlusTag)),
  };

  const tokenJson = JSON.stringify(token);
  const tokenBytes = enc.encode(tokenJson);
  return bytesToB64u(tokenBytes);
}

/**
 * Decrypt a string that was encrypted with encrypt()
 * @param {string} encryptedData - Base64url encoded encrypted data
 * @param {string} passphrase - The passphrase used for encryption
 * @returns {Promise<string>} - The decrypted plaintext
 */
export async function decrypt(encryptedData, passphrase) {
  try {
    const tokenJson = new TextDecoder().decode(b64uToBytes(encryptedData));
    const token = JSON.parse(tokenJson);
    if (!token?.iv || !token?.data) throw new Error('bad_token');

    const iv = b64uToBytes(token.iv);
    const cipherPlusTag = b64uToBytes(token.data);

    const key = await deriveKey(passphrase);
    const dec = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      key,
      cipherPlusTag
    );

    return new TextDecoder().decode(new Uint8Array(dec));
  } catch (error) {
    console.warn('[decrypt] failed:', error);
    throw error;
  }
}

// *******************************************************************************
// BASE64 DECODE UTILITIES (for development/debugging)
// *******************************************************************************

/**
 * Decodes a Base64 string to JSON object
 * Automatically handles URL-encoded strings (single or double encoded)
 * @param {string} encoded - Base64 string (optionally URL-encoded)
 * @returns {object|null} - Decoded JSON object or null if failed
 */
export const decodeBase64 = encoded => {
  if (!encoded) return null;

  const attempts = [
    // 1. Try direct Base64 decode
    () => atob(encoded),
    // 2. Try single URL decode + Base64
    () => atob(decodeURIComponent(encoded)),
    // 3. Try double URL decode + Base64
    () => atob(decodeURIComponent(decodeURIComponent(encoded))),
  ];

  for (const attempt of attempts) {
    try {
      const decoded = attempt();
      return JSON.parse(decoded);
    } catch {
      // Continue to next attempt
    }
  }

  console.warn('[decodeBase64] Failed to decode:', encoded);
  return null;
};

/**
 * Encodes an object to Base64 string (URL-safe)
 * @param {object} obj - Object to encode
 * @returns {string} - URL-encoded Base64 string
 */
export const encodeBase64 = obj => {
  if (!obj) return '';
  try {
    return encodeURIComponent(btoa(JSON.stringify(obj)));
  } catch {
    console.warn('[encodeBase64] Failed to encode:', obj);
    return '';
  }
};

// Register global helpers for console debugging (development only)
if (typeof window !== 'undefined') {
  window.decode = decodeBase64;
  window.encode = encodeBase64;
}

// *******************************************************************************
// REGISTRATION LINK GENERATOR
// *******************************************************************************

/**
 * Generates a temporary registration link with encrypted token
 * The token contains the email and expiration timestamp, encrypted with AES-GCM
 *
 * @param {Object} options - Options for generating the link
 * @param {string} options.email - The email address authorized to register
 * @param {number} [options.expiresInHours=72] - Hours until the link expires (default: 72)
 * @param {string} [options.baseUrl] - Base URL for the link (default: current origin)
 * @returns {Promise<{link: string, hash: string, expiresAt: Date, expiresAtISO: string}>}
 *
 * @example
 * // Generate a registration link valid for 48 hours
 * const { link, expiresAt } = await generateRegistrationLink({
 *   email: 'user@example.com',
 *   expiresInHours: 48
 * });
 * console.log(link); // https://app.example.com/auth/register?hash=...
 * console.log(expiresAt); // Date object
 */
export async function generateRegistrationLink({ email, expiresInHours = 72, baseUrl } = {}) {
  if (!email) {
    throw new Error('Email is required to generate registration link');
  }

  const passphrase = import.meta.env.VITE_CRYPTO_KEY || 'jarvisly-key-word';
  const expiresAt = new Date(Date.now() + expiresInHours * 60 * 60 * 1000);

  const payload = {
    email: email.toLowerCase().trim(),
    exp: expiresAt.getTime(),
    type: 'registration', // Identifies this as a registration invite (not subscription invite)
  };

  const hash = await encrypt(JSON.stringify(payload), passphrase);

  // Determine base URL
  const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  const link = `${origin}/auth/register?hash=${encodeURIComponent(hash)}`;

  return {
    link,
    hash,
    expiresAt,
    expiresAtISO: expiresAt.toISOString(),
  };
}

// Register generateRegistrationLink globally for console use (localhost only)
if (typeof window !== 'undefined') {
  const isLocalhost =
    window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  if (isLocalhost) {
    window.generateRegistrationLink = generateRegistrationLink;
  }
}
